import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronsUpDownIcon,
  ChevronUpIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
} from 'lucide-react';
import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';

import { sidebarRowBase, sidebarRowHover } from '@/app/sidebar-primitives.js';
import { Badge } from '@/components/ui/badge.js';
import { useHintTarget } from '@/features/onboarding/useHintTarget.js';
import { useSharePointNeedsFolders } from '@/features/sharepoint/selected-folders.js';
import { Button } from '@/components/ui/button.js';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Input } from '@/components/ui/input.js';
import { IconFromKey } from '@/features/quick-actions/IconFromKey.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { WorkspaceAboutDialog } from '@/features/workspace/WorkspaceAboutDialog.js';
import { NewWorkspaceDialog } from '@/features/workspace/NewWorkspaceDialog.js';
import { resolveDefaultWorkspaceId } from '@/features/workspace/workspace-access.js';
import {
  orderSwitcherWorkspaces,
  pinnedIds,
  swapped,
  withoutPinned,
  withPinned,
} from '@/features/workspace/workspace-order.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';
import {
  useSetWorkspacePins,
  workspacesQueryOptions,
  type Workspace,
} from '@/lib/api/workspaces.js';
import { cn } from '@/lib/utils.js';

/** A small color-tinted square holding the workspace's Lucide icon (falls back to a building glyph). */
function WorkspaceGlyph({ workspace }: { workspace: Workspace }): JSX.Element {
  return (
    <span
      className="flex size-5 shrink-0 items-center justify-center rounded-md text-white"
      style={{
        backgroundColor: workspace.color ?? 'var(--color-muted-foreground)',
      }}
    >
      <IconFromKey
        iconKey={workspace.icon ?? 'Boxes'}
        className="size-3.5"
        strokeWidth={2}
        aria-hidden
      />
    </span>
  );
}

/** One pin or move action of an active row, shown in its hover strip and in its long-press menu. */
interface PinAction {
  /** The accessible name of the strip button. It names the workspace, because the strip sits beside it. */
  label: string;
  /** The text of the long-press menu item, which belongs to one row already. */
  menuLabel: string;
  icon: ReactNode;
  /** The whole pin list to send. Absent when the action cannot apply, which disables it. */
  pins?: string[];
}

/**
 * One pin or move button beside a workspace row. It is a menu item of its own, not a button inside the
 * row, because Radix moves focus only between registered items. Selecting it keeps the menu open, and
 * `textValue` keeps it out of the typeahead, as on the *What is it?* item. `data-pin-control` keeps it
 * out of the search box's arrow-key targets, because it is hidden until its row is hovered or focused.
 */
function PinControl({
  label,
  disabled,
  onActivate,
  children,
}: {
  label: string;
  disabled: boolean;
  onActivate: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <DropdownMenuItem
      aria-label={label}
      textValue="?"
      data-pin-control
      disabled={disabled}
      className="size-7 shrink-0 justify-center p-0 text-muted-foreground"
      onSelect={(event) => {
        event.preventDefault();
        onActivate();
      }}
    >
      {children}
    </DropdownMenuItem>
  );
}

interface WorkspaceSwitcherProps {
  /** Called after a selection so the mobile drawer can close. */
  onNavigate?: () => void;
  /**
   * Collapsed rail mode: render an icon-only affordance (just the workspace glyph as the dropdown
   * trigger, no label/chevron/gear) so the switcher stays reachable when the sidebar is collapsed
   * (architecture §13 — one switcher visible on every screen). The dropdown itself is unchanged.
   */
  collapsed?: boolean;
}

/**
 * Global workspace switcher — top of the sidebar, visible on every screen. Lists the workspaces the
 * caller belongs to and pins the choice in the persisted UI store (`selectedWorkspaceId`), so every
 * screen reads the same scope.
 *
 * Global-workspaces phase 2: there is no "All workspaces" sentinel — the selection is always a
 * concrete workspace (there is no chat without a workspace). When nothing valid is selected, the
 * switcher defaults to the user's personal workspace (`kind === 'personal'`). A gear button links to
 * the management page; a lightweight dialog creates a new workspace via `POST /v1/workspaces`.
 *
 * Next to the menu's "Workspaces" label sits a *What is it?* link opening `WorkspaceAboutDialog`. It is
 * a real `DropdownMenuItem`, not a button inside the label: Radix menu 2.1.16 prevents Tab inside an open
 * menu and moves focus only between registered items, so a plain button there would be mouse-only.
 * Selecting an item is also what makes Radix close the menu before the dialog opens — the same shape the
 * "New workspace…" item uses — so the menu never sits open behind the modal and focus returns to the
 * trigger on close. The link is visible to every role (nothing here gates on membership role) and the
 * zero-workspace state offers it too, where a newcomer has the least context; only the collapsed rail
 * drops it, having no room for a second affordance.
 *
 * The active list runs Personal first, then the caller's pinned workspaces in their own order under a
 * *Pinned* label, then the rest in the server's name order (MEDA-1033). Every active row but Personal
 * has a Pin or Unpin button, and a pinned row also has Move up and Move down, disabled at the ends of
 * the group. The buttons show only while a mouse is on the row or the row has keyboard focus, so the
 * name takes the full row otherwise. A touch screen never shows them: a long press on the row opens a
 * menu with the same actions, as a right-click does. Each change sends the whole pin list, archived
 * pins included, so an archived workspace keeps its place for a restore. The buttons show only once
 * the service sends `pinned_position`.
 */
export function WorkspaceSwitcher({
  onNavigate,
  collapsed = false,
}: WorkspaceSwitcherProps): JSX.Element {
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const setSelectedWorkspaceId = useUiStore((s) => s.setSelectedWorkspaceId);
  /**
   * Whether this workspace holds a connector the caller still has to point at something. The same
   * question the Connectors tab and the connector card ask, so the three marks agree all the way in.
   */
  const needsFolders = useSharePointNeedsFolders(selectedWorkspaceId);
  const { data: workspaces, refetch: refreshWorkspaces } = useQuery(
    workspacesQueryOptions(),
  );
  const setPins = useSetWorkspacePins();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  /** The parsed (route-validated) search object — see the deep-link effect below for why not searchStr. */
  const routerSearch = useRouterState({
    select: (state) => state.location.search as Record<string, unknown>,
  });

  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const workspaceListRef = useRef<HTMLDivElement>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  /**
   * True while the dialog was opened by the `?create_workspace=1` deep link. That flow lands the
   * new workspace's owner on its manage page after Create — someone sent here by the Secretary's
   * card came to set a workspace up, not to chat. A dropdown-initiated create keeps the old
   * stay-in-place behaviour.
   */
  const [openedByDeepLink, setOpenedByDeepLink] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  /**
   * Where focus returns from the explainer. Attached to the dropdown trigger and to the empty state's
   * own link — the two branches are mutually exclusive, so exactly one of them is mounted and the ref
   * always holds the element the user opened the dialog from.
   */
  const aboutReturnFocusRef = useRef<HTMLButtonElement>(null);

  const all = workspaces ?? [];
  const active = all.filter((workspace) => workspace.status === 'active');
  const pinsSupported = all.some(
    (workspace) => workspace.pinnedPosition !== undefined,
  );
  const storedPins = pinnedIds(all);
  const archived = all.filter((workspace) => workspace.status === 'archived');
  const showWorkspaceSearch = active.length + archived.length > 6;
  const query = showWorkspaceSearch
    ? workspaceSearch.trim().toLocaleLowerCase()
    : '';
  const matchesSearch = (workspace: Workspace): boolean =>
    workspace.name.toLocaleLowerCase().includes(query);
  const matchingActive = active.filter(matchesSearch);
  const matchingArchived = archived.filter(matchesSearch);
  const groups = orderSwitcherWorkspaces(matchingActive);
  // The server lazily provisions a personal workspace on `GET /v1/workspaces`, so a truly empty list
  // is rare — but defend against it: show a clear create CTA instead of a dead "Workspace" label with
  // no reachable gear. Gate on `workspaces` being defined so the loading state doesn't flash the CTA.
  const hasNoWorkspaces = workspaces !== undefined && all.length === 0;

  const createWorkspaceHintRef = useHintTarget<HTMLButtonElement>(
    'nav.workspaces.create',
    'gate',
  );
  const switchWorkspaceHintRef = useHintTarget<HTMLButtonElement>(
    'nav.workspaces',
    'gate',
  );
  const setDropdownTriggerRef = (node: HTMLButtonElement | null): void => {
    switchWorkspaceHintRef(node);
    aboutReturnFocusRef.current = node;
  };

  /**
   * `?create_workspace=1` opens the New workspace dialog straight away — the deep link the Secretary's
   * Teams workspace card sends someone to when the workspace they need does not exist yet. The flag is
   * read from the router's PARSED search, never the raw query string: the default parser JSON-parses
   * each value (the literal `1` arrives as a number) and the router rewrites the URL to the validated
   * search on load, so a raw-string read misses the param the router already consumed — which is
   * exactly how this deep link silently died once. The param is stripped with a replace navigation so
   * a reload does not re-open the dialog; other params are kept. The supported entry route is `/home`
   * — the URL the Secretary's card links to, and the route whose `validateSearch` declares the key. A
   * route that defines its own `validateSearch` without this key (`/chats`, `/quality`,
   * `/workspaces/$id/manage`) drops it before this effect runs, so the deep link only also works on
   * routes with no search validation of their own.
   */
  useEffect(() => {
    const flag = routerSearch.create_workspace;
    if (flag !== '1' && flag !== 1) return;

    setNewOpen(true);
    setOpenedByDeepLink(true);
    void navigate({
      to: pathname,
      search: (prev) => ({ ...prev, create_workspace: undefined }),
      replace: true,
    });
  }, [routerSearch, pathname, navigate]);

  // Resolve the default selection once workspaces load: keep a valid selection, else fall back to the
  // personal workspace (global-workspaces phase 2 has no "All workspaces" sentinel). Runs in an
  // effect so the store update never fires during another component's render.
  const resolvedId = workspaces
    ? resolveDefaultWorkspaceId(all, selectedWorkspaceId)
    : null;
  useEffect(() => {
    if (resolvedId !== null && resolvedId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(resolvedId);
    }
  }, [resolvedId, selectedWorkspaceId, setSelectedWorkspaceId]);

  const selected =
    all.find(
      (workspace) => workspace.id === (resolvedId ?? selectedWorkspaceId),
    ) ?? null;

  /**
   * Leave an open chat when the user picks another workspace. Done in the click handler (not only a
   * route effect): ChatsPage's store-driven navigate is unreliable against adopt/`prevWorkspaceIdRef`
   * races, so `/home` must win here before a stale `?conversationId` can stick.
   */
  function leaveOpenChatIfNeeded(): void {
    useChatSessionStore.getState().setActiveConversationId(null);
    useChatSessionStore.getState().setStreamingConvId(null);
    if (pathname === '/chats') {
      void navigate({ to: '/home', replace: true });
    }
  }

  function select(id: string): void {
    if (id === selectedWorkspaceId) {
      onNavigate?.();
      return;
    }
    setSelectedWorkspaceId(id, 'user');
    leaveOpenChatIfNeeded();
    onNavigate?.();
  }

  async function openSimilarWorkspace(id: string): Promise<void> {
    const result = await refreshWorkspaces();
    if (result.isError)
      throw new Error(
        'Could not refresh your workspace access. Please try again.',
      );
    if (
      !result.data?.some(
        (workspace) => workspace.id === id && workspace.status === 'active',
      )
    )
      throw new Error('Workspace access changed. Please check the name again.');
    select(id);
  }

  /**
   * The pin actions of one active row: Pin for an unpinned row; Move up, Move down and Unpin for the
   * pinned row at `pinnedIndex`. A move swaps the row with the pinned row shown next to it.
   */
  function pinActions(workspace: Workspace, pinnedIndex?: number): PinAction[] {
    if (!pinsSupported) return [];
    if (pinnedIndex === undefined) {
      return [
        {
          label: `Pin ${workspace.name}`,
          menuLabel: 'Pin',
          icon: <PinIcon />,
          pins: withPinned(storedPins, workspace.id),
        },
      ];
    }
    const previous = groups.pinned[pinnedIndex - 1];
    const next = groups.pinned[pinnedIndex + 1];
    return [
      {
        label: `Move ${workspace.name} up`,
        menuLabel: 'Move up',
        icon: <ChevronUpIcon />,
        pins: previous && swapped(storedPins, workspace.id, previous.id),
      },
      {
        label: `Move ${workspace.name} down`,
        menuLabel: 'Move down',
        icon: <ChevronDownIcon />,
        pins: next && swapped(storedPins, workspace.id, next.id),
      },
      {
        label: `Unpin ${workspace.name}`,
        menuLabel: 'Unpin',
        icon: <PinOffIcon />,
        pins: withoutPinned(storedPins, workspace.id),
      },
    ];
  }

  /** Sends the pin list of an action, unless the action is disabled. */
  function activate(action: PinAction): void {
    if (action.pins) setPins.mutate(action.pins);
  }

  /** The menu a long press or a right-click on a row opens, with the row's pin actions as text. */
  function pinMenu(actions: PinAction[]): JSX.Element {
    return (
      <ContextMenuContent>
        {actions.map((action) => (
          <ContextMenuItem
            key={action.menuLabel}
            disabled={!action.pins}
            onSelect={() => activate(action)}
          >
            {action.icon}
            {action.menuLabel}
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
    );
  }

  /**
   * The row's pin buttons. They take no room until a mouse is on the row or the row has keyboard
   * focus, so the name runs the full width otherwise. Tailwind emits `group-hover` inside
   * `@media (hover: hover)`, so a touch screen never shows them and uses the long-press menu.
   */
  function pinStrip(actions: PinAction[]): JSX.Element {
    return (
      <div className="hidden shrink-0 group-hover/wsrow:flex group-has-[:focus-visible]/wsrow:flex">
        {actions.map((action) => (
          <PinControl
            key={action.menuLabel}
            label={action.label}
            disabled={!action.pins}
            onActivate={() => activate(action)}
          >
            {action.icon}
          </PinControl>
        ))}
      </div>
    );
  }

  /** One active workspace row, with its pin buttons and long-press menu when it has pin actions. */
  function activeRow(
    workspace: Workspace,
    actions: PinAction[] = [],
  ): JSX.Element {
    const item = (
      <DropdownMenuItem
        className="min-w-0 flex-1"
        onSelect={() => select(workspace.id)}
      >
        <WorkspaceGlyph workspace={workspace} />
        <span className="flex-1 truncate">{workspace.name}</span>
        {workspace.kind === 'personal' && (
          <Badge variant="secondary">Personal</Badge>
        )}
        {selected?.id === workspace.id && (
          <CheckIcon className="size-4 text-primary" />
        )}
      </DropdownMenuItem>
    );
    if (actions.length === 0) {
      return (
        <div key={workspace.id} className="flex items-center">
          {item}
        </div>
      );
    }
    return (
      <div key={workspace.id} className="group/wsrow flex items-center">
        <ContextMenu>
          <ContextMenuTrigger asChild>{item}</ContextMenuTrigger>
          {pinMenu(actions)}
        </ContextMenu>
        {pinStrip(actions)}
      </div>
    );
  }

  /** Closing the dialog without creating also forgets the deep-link origin. */
  function handleNewOpenChange(open: boolean): void {
    setNewOpen(open);
    if (!open) setOpenedByDeepLink(false);
  }

  function createdNewWorkspace(created: Workspace): void {
    setSelectedWorkspaceId(created.id, 'user');
    leaveOpenChatIfNeeded();
    setNewOpen(false);
    setNewName('');
    if (openedByDeepLink) {
      setOpenedByDeepLink(false);
      void navigate({
        to: '/workspaces/$id/manage',
        params: { id: created.id },
      });
    }
    onNavigate?.();
  }

  return (
    <>
      {hasNoWorkspaces ? (
        <div
          className={cn('flex flex-col gap-0.5', collapsed && 'items-center')}
        >
          <button
            ref={createWorkspaceHintRef}
            type="button"
            onClick={() => setNewOpen(true)}
            className={cn(
              sidebarRowBase,
              sidebarRowHover,
              'w-full min-w-0',
              collapsed && 'justify-center',
            )}
            aria-label="Create your first workspace"
          >
            <PlusIcon className="size-4 shrink-0 text-muted-foreground" />
            {!collapsed && (
              <span className="flex-1 truncate text-left font-medium text-foreground">
                Create your first workspace
              </span>
            )}
          </button>
          {!collapsed && (
            <Button
              ref={aboutReturnFocusRef}
              variant="link"
              size="sm"
              className="h-auto self-start px-2 py-0.5 text-xs font-normal text-muted-foreground"
              aria-label="What is a workspace?"
              onClick={() => setAboutOpen(true)}
            >
              What is it?
            </Button>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'flex items-center gap-1',
            collapsed && 'justify-center',
          )}
        >
          <DropdownMenu onOpenChange={() => setWorkspaceSearch('')}>
            <DropdownMenuTrigger asChild>
              <button
                ref={setDropdownTriggerRef}
                type="button"
                className={cn(
                  sidebarRowBase,
                  sidebarRowHover,
                  collapsed ? 'justify-center' : 'min-w-0 flex-1',
                )}
                aria-label={`Switch workspace (current: ${selected?.name ?? 'none'})`}
              >
                {selected ? (
                  <WorkspaceGlyph workspace={selected} />
                ) : (
                  <span className="size-5 shrink-0 rounded-md bg-muted" />
                )}
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate text-left font-medium text-foreground">
                      {selected?.name ?? 'Workspace'}
                    </span>
                    <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              collisionPadding={8}
              className="flex w-72 max-w-[calc(100vw-1rem)] flex-col"
              style={{
                maxHeight:
                  'min(28rem, var(--radix-dropdown-menu-content-available-height))',
              }}
              onKeyDown={(event) => {
                if (showWorkspaceSearch && event.key === 'Tab') {
                  event.preventDefault();
                  searchInputRef.current?.focus();
                }
              }}
            >
              <DropdownMenuGroup className="flex shrink-0 items-center justify-between gap-1">
                <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                <DropdownMenuItem
                  className="px-2 py-0.5 text-xs font-normal text-muted-foreground"
                  aria-label="What is a workspace?"
                  /**
                   * Kept out of the menu's typeahead. Radix matches typed characters against each
                   * item's text and this row sits above the list, so typing `w` to reach a
                   * workspace called `Work` landed on the explainer and Enter opened the dialog
                   * instead of switching. `textValue` is what the item is matched by, so a
                   * character nobody types to pick a workspace takes it out of the way while the
                   * visible label and the accessible name stay as they are.
                   */
                  textValue="?"
                  onSelect={() => setAboutOpen(true)}
                >
                  What is it?
                </DropdownMenuItem>
              </DropdownMenuGroup>

              {showWorkspaceSearch && (
                <div className="relative mx-1 mb-2 shrink-0">
                  <SearchIcon
                    aria-hidden
                    className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground"
                  />
                  <Input
                    ref={searchInputRef}
                    aria-label="Search workspaces"
                    placeholder="Search workspaces…"
                    className="h-9 pl-9 text-base sm:text-sm"
                    value={workspaceSearch}
                    onChange={(event) => {
                      setWorkspaceSearch(event.target.value);
                      workspaceListRef.current?.scrollTo?.({ top: 0 });
                    }}
                    onKeyDown={(event) => {
                      /** Let Escape close the menu; keep text editing out of Radix typeahead. */
                      if (event.key === 'Escape') return;
                      event.stopPropagation();
                      if (['ArrowDown', 'ArrowUp', 'Tab'].includes(event.key)) {
                        event.preventDefault();
                        const items =
                          workspaceListRef.current?.querySelectorAll<HTMLElement>(
                            '[role="menuitem"]:not([data-pin-control])',
                          );
                        const index =
                          event.key === 'ArrowUp' || event.shiftKey
                            ? (items?.length ?? 0) - 1
                            : 0;
                        const target =
                          items?.[index] ??
                          event.currentTarget
                            .closest('[role="menu"]')
                            ?.querySelector<HTMLElement>(
                              '[data-workspace-create]',
                            );
                        target?.focus();
                      }
                    }}
                  />
                </div>
              )}
              <div
                ref={workspaceListRef}
                className="min-h-0 overflow-y-auto overscroll-contain"
                style={{ WebkitOverflowScrolling: 'touch' }}
                onPointerMoveCapture={(event) => {
                  /** Hovering a row while typing must not steal the input's focus. */
                  if (
                    event.pointerType === 'mouse' &&
                    document.activeElement === searchInputRef.current
                  )
                    event.preventDefault();
                }}
              >
                {groups.personal.map((workspace) => activeRow(workspace))}

                {groups.pinned.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-mini font-normal text-muted-foreground">
                      Pinned
                    </DropdownMenuLabel>
                    {groups.pinned.map((workspace, index) =>
                      activeRow(workspace, pinActions(workspace, index)),
                    )}
                    {groups.others.length > 0 && <DropdownMenuSeparator />}
                  </>
                )}

                {groups.others.map((workspace) =>
                  activeRow(workspace, pinActions(workspace)),
                )}

                {matchingArchived.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-mini font-normal text-muted-foreground">
                      Archived
                    </DropdownMenuLabel>
                    {matchingArchived.map((workspace) => (
                      <DropdownMenuItem
                        key={workspace.id}
                        className="text-muted-foreground"
                        onSelect={() => select(workspace.id)}
                      >
                        <WorkspaceGlyph workspace={workspace} />
                        <span className="flex-1 truncate">
                          {workspace.name}
                        </span>
                        {selected?.id === workspace.id && (
                          <CheckIcon className="size-4 text-primary" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {matchingActive.length === 0 &&
                  matchingArchived.length === 0 && (
                    <p
                      role="status"
                      className="px-2 py-6 text-center text-sm text-muted-foreground"
                    >
                      No workspaces found
                    </p>
                  )}
              </div>

              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-workspace-create
                className="shrink-0"
                onSelect={() => setNewOpen(true)}
              >
                <PlusIcon className="size-4" />
                <span>New workspace…</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {selected && !collapsed && (
            <Button
              asChild
              variant="ghost"
              size="icon-sm"
              aria-label={
                needsFolders
                  ? `Manage ${selected.name} — a connector needs setting up`
                  : `Manage ${selected.name}`
              }
            >
              <Link
                className="relative"
                to="/workspaces/$id/manage"
                params={{ id: selected.id }}
                onClick={onNavigate}
              >
                <SettingsIcon className="size-4" strokeWidth={1.75} />
                {/*
                  The mark says the workspace holds something unfinished; what it is, is said in
                  words on the Connectors tab this leads to. It carries no text of its own because
                  the button's label already carries it, and two voices saying the same thing to a
                  screen reader is one too many.
                */}
                {needsFolders && (
                  <span
                    aria-hidden
                    className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-red-500"
                  />
                )}
              </Link>
            </Button>
          )}
        </div>
      )}

      <WorkspaceAboutDialog
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        returnFocusRef={aboutReturnFocusRef}
      />

      {newOpen && (
        <NewWorkspaceDialog
          name={newName}
          onNameChange={setNewName}
          onCreated={createdNewWorkspace}
          onOpenChange={handleNewOpenChange}
          onOpenWorkspace={openSimilarWorkspace}
        />
      )}
    </>
  );
}
